import { LoadingSpinner } from "@msys/ui";
import CheckIcon from "@mui/icons-material/Check";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import { getCategoryIcon } from "../../categories/categoryIcons.js";
import { removeSubcategoriesOfDeletedParent } from "../../categories/helpers.js";
import { useCategories } from "../../categories/useCategories.js";
import { PROJECT_TYPE_ICONS } from "../../projects/projectTypeIcons.js";
import {
  buildTaxonomyIndex,
  matrixCellKey,
  parseMatrixCellKey,
  prefillBufferFromVerticals,
  type CatalogueBuffer,
} from "../offeredServices/catalogueModel.js";
import { OFFERED_PROJECT_TYPES } from "../offeredServices/projectTypes.js";
import type {
  OnboardingFlowStage,
  OnboardingViewer,
} from "./OnboardingFlowModal.js";
import { OnboardingMatrixModal } from "./OnboardingMatrixModal.js";
import { OnboardingStageLayout } from "./OnboardingStageLayout.js";
import { OnboardingTile } from "./OnboardingTile.js";
import { OnboardingTileGrid } from "./OnboardingTileGrid.js";
import { getVerticalSublineKey } from "./onboardingCategoryHelpers.js";
import {
  ONBOARDING_COLORS,
  ONBOARDING_SMALL_TEXT_BUTTON,
} from "./onboardingDesign.js";
import type {
  OnboardingFlowWizardAction,
  OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";
import { useOnboardingStepLeave } from "./onboardingStepLeave.js";
import { useSetOrganisationOfferedServices } from "./useSetOrganisationOfferedServices.js";

interface Props {
  // The modal's already-loaded viewer; the stage must not re-subscribe to the
  // modal query (each subscription can refetch the suggestions field).
  viewer: OnboardingViewer;
  state: OnboardingFlowWizardState;
  dispatch: React.Dispatch<OnboardingFlowWizardAction>;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

// The small text button of the design handover: 28px high, 12px/20px, uppercase,
// pulled left so its label lines up with the chips above.
const ADJUST_BUTTON_SX = {
  ...ONBOARDING_SMALL_TEXT_BUTTON,
  height: 28,
  ml: "-5px",
  fontSize: 12,
  lineHeight: "20px",
  letterSpacing: "0.4px",
} as const;

// "Reset to work types": the design's underlined text link.
const RESET_LINK_SX = {
  minWidth: 0,
  height: "auto",
  p: 0,
  fontSize: "inherit",
  lineHeight: "inherit",
  letterSpacing: "inherit",
  fontWeight: 500,
  textTransform: "none",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  color: ONBOARDING_COLORS.dsNavy,
  "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
} as const;

// The check that replaces a selected work type's icon: a 20px navy disc.
const WORK_TYPE_CHECK_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "50%",
  bgcolor: ONBOARDING_COLORS.dsNavy,
  color: "common.white",
} as const;

// A work type chip of the design: 44px pill, 14px/500, light-blue and inset
// ring once selected.
const workTypeChipSx = (selected: boolean) =>
  ({
    height: 44,
    borderRadius: "22px",
    pl: "12px",
    pr: "16px",
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "normal",
    color: ONBOARDING_COLORS.heading,
    bgcolor: selected ? ONBOARDING_COLORS.dsTint : "common.white",
    borderColor: selected
      ? ONBOARDING_COLORS.dsNavy
      : ONBOARDING_COLORS.fieldBorder,
    ...(selected && {
      boxShadow: `inset 0 0 0 1px ${ONBOARDING_COLORS.dsNavy}`,
    }),
    "&:hover": {
      bgcolor: selected ? ONBOARDING_COLORS.dsTint : "common.white",
      borderColor: ONBOARDING_COLORS.dsNavy,
    },
    "& .MuiChip-label": { px: 0 },
    "& .MuiChip-icon": {
      ml: 0,
      mr: "8px",
      fontSize: 18,
      color: selected ? "common.white" : ONBOARDING_COLORS.dsNavy,
    },
  }) as const;

const SectionHeading = ({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) => (
  <Typography
    id={id}
    component="h2"
    sx={{
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: "normal",
      color: ONBOARDING_COLORS.heading,
    }}
  >
    {children}
  </Typography>
);

// How many work types the commit would write: the chips, or - once the
// services are customised - the distinct types left in the applied matrix.
const workTypeCountFor = (state: OnboardingFlowWizardState) =>
  state.applyToAll === "no"
    ? new Set(
        state.matrixSelections
          .filter(s => state.matrixDeclaredCategories.includes(s.categoryId))
          .map(s => s.projectType)
      ).size
    : state.selectedProjectTypes.length;

export const OnboardingFlowVerticalsStage = ({
  viewer,
  state,
  dispatch,
  setStage,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile", "ProjectTypes"]);
  const {
    categories: allCategories,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useCategories();
  const tradesHeadingId = React.useId();
  const workTypesHeadingId = React.useId();
  const workTypesHintId = React.useId();
  // Set by a Continue without any work type; cleared as soon as one is chosen.
  const [workTypeMissing, setWorkTypeMissing] = React.useState(false);
  const workTypesGroupRef = React.useRef<HTMLDivElement>(null);
  const firstWorkTypeChipRef = React.useRef<HTMLDivElement>(null);
  // The draft the matrix modal starts from; `null` while the modal is closed.
  const [matrixSeed, setMatrixSeed] = React.useState<CatalogueBuffer | null>(
    null
  );

  const verticals = React.useMemo(
    () =>
      allCategories
        .filter(c => !c.parentId)
        .slice()
        .sort((a, b) => a.rank - b.rank),
    [allCategories]
  );
  // The taxonomy behind the "all services" commit (it cascades the work types
  // onto the leaves of the chosen trades), the service count and the modal.
  const usableCategories = React.useMemo(
    () => removeSubcategoriesOfDeletedParent(allCategories, allCategories),
    [allCategories]
  );
  const index = React.useMemo(
    () => buildTaxonomyIndex(usableCategories),
    [usableCategories]
  );

  const {
    commit,
    error: commitError,
    loading,
  } = useSetOrganisationOfferedServices(state, setStage, index);

  // Trades without any work type cannot be committed: Continue (and a jump
  // from the rail) points at the chips instead. Once customised, a matrix
  // without a cell on any declared row counts as missing too; the chips stay
  // the target there, as they set a type on every declared leaf and sit right
  // above the matrix entry point.
  const workTypeMissingFor = (current: OnboardingFlowWizardState) =>
    current.selectedVerticals.length > 0 && workTypeCountFor(current) === 0;
  const showWorkTypeMissing = () => {
    setWorkTypeMissing(true);
    workTypesGroupRef.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
    firstWorkTypeChipRef.current?.focus({ preventScroll: true });
  };

  // A jump from the rail to the business step keeps the choice in the wizard
  // (it comes back with the step) without committing it: a committed
  // organisation skips this step from then on. Like Continue, a choice that
  // cannot be committed keeps the user here.
  useOnboardingStepLeave(target => {
    if (loading) {
      return;
    }
    if (workTypeMissingFor(state)) {
      showWorkTypeMissing();
      return;
    }
    setStage(target);
  });

  // Defense-in-depth auto-skip for direct-URL navigation. CLIENT orgs and orgs
  // with pre-existing offered services should never see this stage: the
  // primary CLIENT routing branches at the form stage, and populated orgs are
  // excluded here so the bulk REPLACE-ALL setOrganisationOfferedServices
  // mutation never clobbers their existing rows. Both produce the same
  // `?ofs=success` outcome.
  React.useEffect(() => {
    if (
      viewer.organisation.organisationType !== "CRAFTSMAN" ||
      viewer.organisation.offeredServices.length > 0
    ) {
      setStage("success");
    }
  }, [viewer, setStage]);

  if (
    viewer.organisation.organisationType !== "CRAFTSMAN" ||
    viewer.organisation.offeredServices.length > 0
  ) {
    return null;
  }

  const customised = state.applyToAll === "no";
  const selectedTrades = new Set(state.selectedVerticals);
  const selectedTypes = new Set(state.selectedProjectTypes);
  const declaredLeafIds = state.matrixDeclaredCategories.filter(id =>
    index.isLeaf(id)
  );

  const projectTypeLabels: Record<OfferedProjectType, string> = {
    NEW: t("New installation", { ns: "OrganisationProfile" }),
    REPAIR: t("Repair", { ns: "OrganisationProfile" }),
    RENOVATION: t("Renovation", { ns: "OrganisationProfile" }),
    MAINTENANCE: t("Maintenance", { ns: "OrganisationProfile" }),
    // The same name as in the services matrix and the settings.
    DIY: t("Do it Yourself", { ns: "ProjectTypes" }),
  };

  const projectTypeDescriptions: Record<OfferedProjectType, string> = {
    NEW: t("A new system or building part, from scratch", {
      ns: "OrganisationProfile",
    }),
    REPAIR: t("Something is broken and needs fixing now", {
      ns: "OrganisationProfile",
    }),
    RENOVATION: t("Replacing or modernising what exists", {
      ns: "OrganisationProfile",
    }),
    MAINTENANCE: t("Recurring service and inspections", {
      ns: "OrganisationProfile",
    }),
    DIY: t("You supply material and advice, the owner does the work", {
      ns: "OrganisationProfile",
    }),
  };

  const workTypeCount = workTypeCountFor(state);
  const hasTrades = state.selectedVerticals.length > 0;
  // The "all services" commit reads the taxonomy; committing before it loaded,
  // or after it failed to, would silently drop every selected work type.
  const taxonomyBlocked =
    !customised && (categoriesLoading || categoriesError !== undefined);

  // Continue is never disabled; it validates here. Without a trade it is Skip,
  // with trades but no work type it points at the chips instead of committing.
  const handleContinue = () => {
    if (loading) {
      return;
    }
    if (!hasTrades) {
      setStage("success");
      return;
    }
    if (workTypeMissingFor(state)) {
      showWorkTypeMissing();
      return;
    }
    if (customised) {
      void commit("customised");
      return;
    }
    if (taxonomyBlocked) {
      return;
    }
    void commit("all-services");
  };

  const openMatrix = () =>
    setMatrixSeed(
      customised
        ? {
            declared: new Set(state.matrixDeclaredCategories),
            selections: new Set(
              state.matrixSelections.map(s =>
                matrixCellKey(s.categoryId, s.projectType)
              )
            ),
          }
        : prefillBufferFromVerticals(
            index,
            state.selectedVerticals,
            state.selectedProjectTypes
          )
    );

  const shownError = commitError ?? categoriesError;

  return (
    <OnboardingStageLayout
      stage="verticals"
      contentSpacing="28px"
      compactTop
      organisationType={viewer.organisation.organisationType}
      title={t("What does your business do?", { ns: "OrganisationProfile" })}
      description={`${t(
        "Your trades and work types decide which services we set up for you.",
        { ns: "OrganisationProfile" }
      )} ${t(
        "Everything on this step is optional – you can complete it later.",
        {
          ns: "OrganisationProfile",
        }
      )}`}
      summary={
        hasTrades ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "flex-end",
              fontSize: 14,
              color: ONBOARDING_COLORS.body,
            }}
          >
            <CheckIcon aria-hidden sx={{ fontSize: 20 }} />
            <span>
              {t(
                "{trades, plural, one {# trade} other {# trades}} · {types, plural, one {# work type} other {# work types}}",
                {
                  ns: "OrganisationProfile",
                  trades: state.selectedVerticals.length,
                  types: workTypeCount,
                }
              )}
            </span>
          </Stack>
        ) : (
          t("No trades selected yet – you can add them later in Settings", {
            ns: "OrganisationProfile",
          })
        )
      }
      // The trades are optional: skipping declares nothing and writes nothing.
      skip={{
        label: t("Skip for now", { ns: "OrganisationProfile" }),
        handleClick: () => {
          if (!loading) {
            setStage("success");
          }
        },
      }}
      primary={{
        label: t("Continue", { ns: "Global" }),
        handleClick: handleContinue,
        loading,
      }}
    >
      <Stack spacing="28px">
        <Stack spacing="14px">
          <SectionHeading id={tradesHeadingId}>
            {t("Your trades", { ns: "OrganisationProfile" })}
          </SectionHeading>
          {categoriesLoading ? (
            <LoadingSpinner />
          ) : (
            <Box role="group" aria-labelledby={tradesHeadingId}>
              <OnboardingTileGrid minTileWidth={220} gap={12} maxColumns={3}>
                {verticals.map(vertical => {
                  const sublineKey = getVerticalSublineKey(vertical.id);
                  return (
                    <OnboardingTile
                      key={vertical.id}
                      label={vertical.name}
                      description={
                        sublineKey
                          ? t(sublineKey, { ns: "OrganisationProfile" })
                          : undefined
                      }
                      Icon={getCategoryIcon(vertical.id)}
                      selected={selectedTrades.has(vertical.id)}
                      onToggle={() =>
                        dispatch({
                          type: "toggleVertical",
                          categoryId: vertical.id,
                        })
                      }
                    />
                  );
                })}
              </OnboardingTileGrid>
            </Box>
          )}
        </Stack>

        <Stack>
          <SectionHeading id={workTypesHeadingId}>
            {t("Your work types", { ns: "OrganisationProfile" })}
          </SectionHeading>
          <Typography
            sx={{
              mt: "4px",
              fontSize: 13,
              lineHeight: 1.45,
              letterSpacing: "normal",
              color: ONBOARDING_COLORS.muted,
            }}
          >
            {`${t("Which kinds of jobs do you take on?", {
              ns: "OrganisationProfile",
            })} ${t(
              "Applied to all selected services. You can fine-tune per service below or later in Settings.",
              { ns: "OrganisationProfile" }
            )}`}
          </Typography>
          <Box sx={{ mt: "14px" }}>
            <Stack
              ref={workTypesGroupRef}
              direction="row"
              role="group"
              aria-labelledby={workTypesHeadingId}
              aria-describedby={workTypeMissing ? workTypesHintId : undefined}
              sx={{ flexWrap: "wrap", gap: "10px" }}
            >
              {OFFERED_PROJECT_TYPES.map((projectType, chipIndex) => {
                const selected = selectedTypes.has(projectType);
                const Icon = PROJECT_TYPE_ICONS[projectType];
                return (
                  <Tooltip
                    key={projectType}
                    title={projectTypeDescriptions[projectType]}
                    describeChild
                  >
                    <Chip
                      ref={chipIndex === 0 ? firstWorkTypeChipRef : undefined}
                      clickable
                      variant="outlined"
                      aria-pressed={selected}
                      label={projectTypeLabels[projectType]}
                      icon={
                        selected ? (
                          <Box component="span" sx={WORK_TYPE_CHECK_SX}>
                            <CheckIcon sx={{ fontSize: 12 }} />
                          </Box>
                        ) : (
                          <Icon />
                        )
                      }
                      onClick={() => {
                        setWorkTypeMissing(false);
                        dispatch({
                          type: "toggleProjectType",
                          projectType,
                          declaredLeafIds,
                        });
                      }}
                      sx={workTypeChipSx(selected)}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
            {workTypeMissing && (
              <Typography
                id={workTypesHintId}
                sx={{
                  mt: "8px",
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: ONBOARDING_COLORS.errorText,
                }}
              >
                {t("Select at least one work type", {
                  ns: "OrganisationProfile",
                })}
              </Typography>
            )}
            <Stack
              direction="row"
              sx={{
                mt: "16px",
                flexWrap: "wrap",
                alignItems: "center",
                columnGap: "8px",
                fontSize: 13,
                lineHeight: 1.45,
                letterSpacing: "normal",
                color: ONBOARDING_COLORS.muted,
              }}
            >
              {customised && (
                <>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "inherit",
                      lineHeight: "inherit",
                      letterSpacing: "inherit",
                      color: ONBOARDING_COLORS.body,
                    }}
                  >
                    {t("Customised per service", { ns: "OrganisationProfile" })}
                  </Typography>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{ color: ONBOARDING_COLORS.fieldBorder }}
                  >
                    ·
                  </Box>
                  <Button
                    variant="text"
                    onClick={() => dispatch({ type: "resetCustomisation" })}
                    sx={RESET_LINK_SX}
                  >
                    {t("Reset to work types", { ns: "OrganisationProfile" })}
                  </Button>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{ color: ONBOARDING_COLORS.fieldBorder }}
                  >
                    ·
                  </Box>
                </>
              )}
              <Button
                variant="text"
                disabled={categoriesLoading || categoriesError !== undefined}
                onClick={openMatrix}
                sx={ADJUST_BUTTON_SX}
              >
                {t("Adjust services and work types", {
                  ns: "OrganisationProfile",
                })}
              </Button>
            </Stack>
          </Box>
        </Stack>

        {shownError && (
          <Alert
            severity="error"
            action={
              // The trades, the "all services" commit and the matrix all need
              // the taxonomy; a failed categories query offers a retry.
              !commitError && categoriesError ? (
                <Button
                  color="inherit"
                  size="small"
                  // A failed retry surfaces through `categoriesError`; the
                  // rejected promise itself carries nothing to handle.
                  onClick={() =>
                    void refetchCategories().catch(() => undefined)
                  }
                >
                  {t("Retry", { ns: "Global" })}
                </Button>
              ) : undefined
            }
          >
            {shownError.message ||
              t("Something went wrong. Please try again.", { ns: "Global" })}
          </Alert>
        )}
      </Stack>

      {matrixSeed && (
        <OnboardingMatrixModal
          categories={usableCategories}
          index={index}
          initialBuffer={matrixSeed}
          onCancel={() => setMatrixSeed(null)}
          onApply={buffer => {
            setWorkTypeMissing(false);
            dispatch({
              type: "applyMatrix",
              declaredCategories: [...buffer.declared],
              selections: [...buffer.selections].map(parseMatrixCellKey),
            });
            setMatrixSeed(null);
          }}
        />
      )}
    </OnboardingStageLayout>
  );
};
